// frontend/src/pages/UserManagement.js
import React, { useEffect, useState } from 'react';
import api from '../api';
import { Modal, Button, Form, Table, Spinner, Row, Col, Card, Badge, Pagination, Container } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import '../styles/adminDashboard.css';

const UserManagement = () => {
  const { socket } = useAuth(); // 获取 socket 连接
  
  // 移动端菜单状态
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth <= 768) {
        setSidebarCollapsed(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // 辅助函数：获取日志类型图标
  const getLogTypeIcon = (category) => {
    const iconMap = {
      'admin_add_points': '➕',
      'admin_deduct_points': '➖',
      'admin_add_silver_coins': '🪙',
      'admin_deduct_silver_coins': '💸',
      'game_payout': '🎮',
      'game_bet': '🎯',
      'transfer_in': '📥',
      'transfer_out': '📤',
      'debit': '💰',
      'jackpot': '🎊',
      'system': '⚙️',
      'login': '🔐',
      'logout': '🔓'
    };
    return iconMap[category] || '📋';
  };

  // 辅助函数：获取日志类型名称
  const getLogTypeName = (category) => {
    const nameMap = {
      'admin_add_points': '管理员加分',
      'admin_deduct_points': '管理员减分',
      'admin_add_silver_coins': '管理员加银币',
      'admin_deduct_silver_coins': '管理员减银币',
      'game_payout': '游戏派奖',
      'game_bet': '游戏投注',
      'transfer_in': '转账收入',
      'transfer_out': '转账支出',
      'debit': '下分',
      'jackpot': '奖池中奖',
      'system': '系统操作',
      'login': '登录',
      'logout': '登出'
    };
    return nameMap[category] || category || '未知操作';
  };

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // 分页和搜索状态
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 20
  });
  
  // 搜索和筛选状态
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);

  // 账目（轮次）相关 state
  const [showLedgerModal, setShowLedgerModal] = useState(false);
  const [ledgerUser, setLedgerUser] = useState(null);
  const [userRounds, setUserRounds] = useState([]);
  const [ledgerPage, setLedgerPage] = useState(1);
  const [ledgerTotalPages, setLedgerTotalPages] = useState(1);
  const ledgerLimit = 10;

  // 玩家日志相关（操作日志）
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [logsUser, setLogsUser] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotalPages, setLogsTotalPages] = useState(1);
  const [logsLimit, setLogsLimit] = useState(10);

  // 用户统计数据
  const [userStats, setUserStats] = useState({
    totalRemainingPoints: 0,
    totalRemainingSilverCoins: 0
  });
  const [statsLoading, setStatsLoading] = useState(false);

  // ========== 用户管理操作函数 ========== //

  // 加分/减分模态
  const [showPointsModal, setShowPointsModal] = useState(false);
  const [modalType, setModalType] = useState('add'); // 'add' or 'deduct'
  const [selectedUser, setSelectedUser] = useState(null);
  const [points, setPoints] = useState(0);

  const handleShowPointsModal = (user, type) => {
    setSelectedUser(user);
    setModalType(type);
    setPoints(0);
    setShowPointsModal(true);
  };
  const handleClosePointsModal = () => {
    setShowPointsModal(false);
    setSelectedUser(null);
    setPoints(0);
  };
  const handleSubmitPoints = async () => {
    const parsedPoints = parseFloat(points);
    if (isNaN(parsedPoints) || parsedPoints <= 0) {
      toast.warning('请输入有效的正数积分');
      return;
    }
    const endpoint =
      modalType === 'add'
        ? `/api/admin/users/${selectedUser._id}/add-points`
        : `/api/admin/users/${selectedUser._id}/deduct-points`;
    try {
      const res = await api.post(endpoint, { points: parsedPoints });
      toast.success(res.data.message);
      // 重新获取当前页数据而不是手动更新状态
      await fetchUsers(pagination.currentPage);
      await fetchUserStats(); // 刷新统计数据
      handleClosePointsModal();
    } catch (err) {
      console.error('操作积分失败:', err);
      toast.error(err.response?.data?.message || '操作积分失败');
    }
  };

  // 加银币/减银币模态
  const [showSilverCoinsModal, setShowSilverCoinsModal] = useState(false);
  const [silverCoinsModalType, setSilverCoinsModalType] = useState('add'); // 'add' or 'deduct'
  const [silverCoins, setSilverCoins] = useState(0);

  const handleShowSilverCoinsModal = (user, type) => {
    setSelectedUser(user);
    setSilverCoinsModalType(type);
    setSilverCoins(0);
    setShowSilverCoinsModal(true);
  };
  const handleCloseSilverCoinsModal = () => {
    setShowSilverCoinsModal(false);
    setSelectedUser(null);
    setSilverCoins(0);
  };
  const handleSubmitSilverCoins = async () => {
    const parsedSilverCoins = parseFloat(silverCoins);
    if (isNaN(parsedSilverCoins) || parsedSilverCoins <= 0) {
      toast.warning('请输入有效的正数银币');
      return;
    }
    const endpoint =
      silverCoinsModalType === 'add'
        ? `/api/admin/users/${selectedUser._id}/add-silver-coins`
        : `/api/admin/users/${selectedUser._id}/deduct-silver-coins`;
    try {
      const res = await api.post(endpoint, { silverCoins: parsedSilverCoins });
      toast.success(res.data.message);
      await fetchUsers(pagination.currentPage);
      await fetchUserStats(); // 刷新统计数据
      handleCloseSilverCoinsModal();
    } catch (err) {
      console.error('操作银币失败:', err);
      toast.error(err.response?.data?.message || '操作银币失败');
    }
  };

  // 编辑用户模态
  const [showEditModal, setShowEditModal] = useState(false);
  const [editUsername, setEditUsername] = useState('');
  const [editRole, setEditRole] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const handleShowEditModal = (user) => {
    setSelectedUser(user);
    setEditUsername(user.username);
    setEditRole(user.role);
    setEditStatus(user.status);
    setShowEditModal(true);
  };
  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedUser(null);
    setEditUsername('');
    setEditRole('');
    setEditStatus('');
  };
  const handleSubmitEdit = async () => {
    if (!editUsername.trim()) {
      toast.warning('用户名不能为空');
      return;
    }
    const payload = {
      username: editUsername,
      role: editRole,
      status: editStatus,
    };
    try {
      const res = await api.put(`/api/admin/users/${selectedUser._id}`, payload);
      toast.success(res.data.message);
      await fetchUsers(pagination.currentPage);
      handleCloseEditModal();
    } catch (err) {
      console.error('编辑用户失败:', err);
      toast.error(err.response?.data?.message || '编辑用户失败');
    }
  };

  // 封禁/解封用户模态
  const [showBanModal, setShowBanModal] = useState(false);
  const [banAction, setBanAction] = useState('ban'); // 'ban' or 'unban'
  const handleShowBanModal = (user, action) => {
    setSelectedUser(user);
    setBanAction(action);
    setShowBanModal(true);
  };
  const handleCloseBanModal = () => {
    setShowBanModal(false);
    setSelectedUser(null);
    setBanAction('ban');
  };
  const handleSubmitBan = async () => {
    const endpoint =
      banAction === 'ban'
        ? `/api/admin/users/${selectedUser._id}/ban`
        : `/api/admin/users/${selectedUser._id}/unban`;
    try {
      const res = await api.post(endpoint);
      toast.success(res.data.message);
      await fetchUsers(pagination.currentPage);
      handleCloseBanModal();
    } catch (err) {
      console.error(
        `${banAction === 'ban' ? '封禁' : '解封'}用户失败:`,
        err
      );
      toast.error(err.response?.data?.message || '操作失败');
    }
  };

  // 删除用户
  const handleDelete = async (userId) => {
    if (window.confirm('确定要删除这个用户吗？')) {
      try {
        await api.delete(`/api/admin/users/${userId}`);
        await fetchUsers(pagination.currentPage);
        toast.success('用户删除成功');
      } catch (err) {
        console.error('删除用户失败:', err);
        toast.error(err.response?.data?.message || '删除用户失败');
      }
    }
  };

  // 解除异常
  const handleClearException = async (user) => {
    try {
      const res = await api.post(`/api/admin/users/${user._id}/clear-exception`);
      toast.success(res.data.message || '已解除异常状态');
      await fetchUsers(pagination.currentPage);
    } catch (err) {
      console.error('解除异常失败:', err);
      toast.error(err.response?.data?.message || '解除异常失败');
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchUserStats();
  }, []);

  // 当筛选条件改变时重新获取数据
  useEffect(() => {
    if (!loading) {
      handleFilterChange();
    }
  }, [roleFilter, statusFilter, sortBy, sortOrder]);

  // 实时更新统计数据 - 优化后的更新机制
  useEffect(() => {
    // 减少自动刷新频率到2分钟，避免过度API调用
    const interval = setInterval(() => {
      fetchUserStats();
    }, 120000); // 2分钟刷新一次

    return () => clearInterval(interval);
  }, []);

  // 页面可见性变化时刷新统计数据
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchUserStats(); // 页面重新可见时刷新统计数据
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Socket.IO 实时更新统计数据 - 使用防抖优化
  useEffect(() => {
    if (!socket) return;

    // 防抖函数，避免短时间内多次API调用
    let timeoutId = null;
    const debouncedFetchStats = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        fetchUserStats();
      }, 1000); // 1秒防抖延迟
    };

    // 监听全局统计更新事件（需要后端支持）
    const handleStatsUpdate = () => {
      debouncedFetchStats();
    };

    // 只监听管理员相关的统计更新事件
    socket.on('adminStatsUpdate', handleStatsUpdate);

    // 清理监听器和定时器
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      socket.off('adminStatsUpdate', handleStatsUpdate);
    };
  }, [socket]);

  // 获取用户列表 - 支持搜索、筛选、分页
  const fetchUsers = async (page = 1, resetSearch = false) => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString(),
        sortBy,
        sortOrder
      });
      
      // 搜索参数
      if (searchTerm.trim() && !resetSearch) {
        params.append('search', searchTerm.trim());
      }
      
      // 筛选参数
      if (roleFilter !== 'all') {
        params.append('role', roleFilter);
      }
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      
      const res = await api.get(`/api/admin/users?${params.toString()}`);
      
      // 处理新的API响应格式
      if (res.data.users) {
        setUsers(res.data.users);
        setPagination(res.data.pagination);
      } else {
        // 兼容旧格式
        setUsers(res.data);
      }
    } catch (err) {
      console.error('获取用户列表失败:', err);
      toast.error(err.response?.data?.message || '获取用户列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取用户统计数据 - 优化版本
  const fetchUserStats = async () => {
    try {
      setStatsLoading(true);
      const res = await api.get('/api/admin/users/statistics');
      
      // 数据验证和清理
      const newStats = {
        totalRemainingPoints: Math.max(0, Math.floor(res.data.totalRemainingPoints || 0)),
        totalRemainingSilverCoins: Math.max(0, Math.floor(res.data.totalRemainingSilverCoins || 0)),
        timestamp: res.data.timestamp || new Date().toISOString()
      };
      
      // 只有数据确实发生变化时才更新状态
      setUserStats(prevStats => {
        if (prevStats.totalRemainingPoints !== newStats.totalRemainingPoints ||
            prevStats.totalRemainingSilverCoins !== newStats.totalRemainingSilverCoins) {
          console.log('统计数据已更新:', newStats);
          return newStats;
        }
        return prevStats;
      });
    } catch (err) {
      console.error('获取用户统计失败:', err);
      // 如果是网络错误或服务器错误，显示错误提示
      if (err.response?.status >= 500 || !err.response) {
        toast.error('获取统计数据失败，请检查网络连接', {
          toastId: 'stats-error', // 防止重复显示
          autoClose: 3000
        });
      }
    } finally {
      setStatsLoading(false);
    }
  };

  // 搜索功能
  const handleSearch = async (e) => {
    e.preventDefault();
    await fetchUsers(1);
  };

  const handleResetSearch = async () => {
    setSearchTerm('');
    setRoleFilter('all');
    setStatusFilter('all');
    await fetchUsers(1, true);
  };

  // 分页处理
  const handlePageChange = (page) => {
    fetchUsers(page);
  };

  // 排序处理
  const handleSortChange = (newSortBy) => {
    const newSortOrder = sortBy === newSortBy && sortOrder === 'desc' ? 'asc' : 'desc';
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
  };

  // 筛选器变化处理
  const handleFilterChange = () => {
    fetchUsers(1);
  };

  // 账目分页查看
  const fetchLedger = async (userId, page = 1) => {
    try {
      const res = await api.get(
        `/api/users/${userId}/round-logs?page=${page}&limit=${ledgerLimit}`
      );
      setUserRounds(res.data.logs);
      setLedgerPage(res.data.currentPage);
      setLedgerTotalPages(res.data.totalPages);
    } catch (err) {
      console.error('获取账目失败:', err);
      toast.error(err.response?.data?.message || '获取账目失败');
    }
  };
  const handleShowLedger = (user, page = 1) => {
    setLedgerUser(user);
    setShowLedgerModal(true);
    fetchLedger(user._id, page);
  };
  const goPrevLedgerPage = () => {
    if (ledgerPage <= 1) return;
    fetchLedger(ledgerUser._id, ledgerPage - 1);
  };
  const goNextLedgerPage = () => {
    if (ledgerPage >= ledgerTotalPages) return;
    fetchLedger(ledgerUser._id, ledgerPage + 1);
  };

  // 操作日志分页查看
  const fetchLogs = async (userId, page = 1) => {
    try {
      const res = await api.get(
        `/api/users/${userId}/logs?page=${page}&limit=${logsLimit}`
      );
      setLogs(res.data.logs);
      setLogsPage(res.data.currentPage);
      setLogsTotalPages(res.data.totalPages);
    } catch (err) {
      console.error('获取玩家日志失败:', err);
      toast.error(err.response?.data?.message || '获取玩家日志失败');
    }
  };
  const handleShowLogs = (user, page = 1) => {
    setLogsUser(user);
    setShowLogsModal(true);
    fetchLogs(user._id, page);
  };
  const goPrevLogsPage = () => {
    if (logsPage <= 1) return;
    fetchLogs(logsUser._id, logsPage - 1);
  };
  const goNextLogsPage = () => {
    if (logsPage >= logsTotalPages) return;
    fetchLogs(logsUser._id, logsPage + 1);
  };

  if (loading) return (
    <div style={{textAlign:'center',marginTop:80}}>
      <Spinner animation="border" variant="info" style={{width:60,height:60}}/>
      <div style={{color:'#0cf',marginTop:16,fontSize:18}}>加载中...</div>
    </div>
  );

  // 菜单项配置
  const menuItems = [
    { path: '/admin/users', label: '用户管理', icon: '👥' },
    { path: '/admin/devices', label: '设备管理', icon: '🎮' },
    { path: '/admin/statistics', label: '数据统计', icon: '📊' },
    { path: '/admin/partitions', label: '分区管理', icon: '🏢' },
    { path: '/admin/announcements', label: '公告管理', icon: '📢' },
    { path: '/admin/settings', label: '系统设置', icon: '⚙️' },
    { path: '/admin/reward-policy', label: '银币派奖策略', icon: '🏆' },
    { path: '/admin/logs', label: '日志管理', icon: '📝' },
    { path: '/admin/withdrawals', label: '提现管理', icon: '💰' }
  ];

  return (
    <>
      {/* 移动端菜单按钮 */}
      {isMobile && (
        <button 
          className={`mobile-menu-btn ${!sidebarCollapsed ? 'menu-open' : ''}`}
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        >
          <div className="menu-icon">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <span className="menu-text">
            {sidebarCollapsed ? '菜单' : '关闭'}
          </span>
        </button>
      )}

      {/* 移动端遮罩 */}
      {isMobile && !sidebarCollapsed && (
        <div 
          className="mobile-overlay"
          onClick={() => setSidebarCollapsed(true)}
        />
      )}

      {/* 移动端侧边栏菜单 */}
      {isMobile && (
        <div className={`admin-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
          <div className="sidebar-header">
            <div className="logo-section">
              <div className="logo">🎯</div>
              <h3 className="dashboard-title">管理员后台</h3>
            </div>
            <button 
              className="close-btn"
              onClick={() => setSidebarCollapsed(true)}
            >
              ✕
            </button>
          </div>

          <div className="user-info">
            <div className="avatar">👤</div>
            <div className="user-details">
              <p className="username">管理员面板</p>
              <span className="user-role">系统管理员</span>
            </div>
          </div>

          <nav className="nav-menu">
            {menuItems.map((item) => (
              <a
                key={item.path}
                href={item.path}
                className={`nav-link ${window.location.pathname === item.path ? 'active' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  window.location.href = item.path;
                  setSidebarCollapsed(true);
                }}
              >
                <span className="nav-icon">{item.icon}</span>
                <span className="nav-label">{item.label}</span>
              </a>
            ))}
          </nav>

          <div className="sidebar-actions">
            <button
              className="cs-btn"
              onClick={() => {
                alert('客服功能暂未开放');
                setSidebarCollapsed(true);
              }}
            >
              <span className="btn-icon">💬</span>
              <span className="btn-text">客服会话</span>
            </button>

            <button
              className="logout-btn"
              onClick={() => {
                if (window.confirm('确定要退出登录吗？')) {
                  // 清除登录状态
                  localStorage.removeItem('token');
                  localStorage.removeItem('user');
                  window.location.href = '/login';
                }
              }}
            >
              <span className="btn-icon">🚪</span>
              <span className="btn-text">退出登录</span>
            </button>
          </div>
        </div>
      )}

      <div className="admin-page-container" style={{ 
        padding: '20px', 
        background: 'linear-gradient(120deg,#f8fcff 60%,#e0f7fa 100%)', 
        minHeight: '100vh'
      }}>
      <Container fluid>
        <Row className="mb-4">
          <Col>
            <h2 style={{
              color:'#0cf',
              fontWeight:900,
              letterSpacing:2,
              textAlign:'center',
              textShadow:'0 2px 8px #0cf8',
              marginBottom: 30
            }}>
              用户管理系统
            </h2>
          </Col>
        </Row>

        {/* 搜索和筛选区域 */}
        <Card className="mb-4" style={{
          background: 'rgba(255,255,255,0.95)',
          border: '2px solid #0cf',
          borderRadius: 15,
          boxShadow: '0 4px 20px rgba(0,204,255,0.3)'
        }}>
          <Card.Header style={{
            background: 'linear-gradient(90deg,#0cf,#0ff)',
            color: 'white',
            fontWeight: 'bold',
            borderRadius: '13px 13px 0 0'
          }}>
            <div className="d-flex justify-content-between align-items-center">
              <span>🔍 搜索与筛选</span>
              <Button 
                variant="outline-light" 
                size="sm"
                onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
              >
                {showAdvancedSearch ? '收起高级筛选' : '展开高级筛选'}
              </Button>
            </div>
          </Card.Header>
          <Card.Body>
            {/* 基础搜索 */}
            <Form onSubmit={handleSearch}>
              <Row className="align-items-end">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>🔍 搜索用户</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="输入用户名、角色或状态进行搜索..."
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      style={{
                        borderRadius: 10,
                        border: '2px solid #0cf',
                        boxShadow: '0 2px 10px rgba(0,204,255,0.2)'
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Button 
                    variant="primary" 
                    type="submit" 
                    style={{
                      background: 'linear-gradient(90deg,#0cf,#0ff)',
                      border: 'none',
                      borderRadius: 10,
                      fontWeight: 'bold',
                      width: '100%'
                    }}
                  >
                    🔍 搜索
                  </Button>
                </Col>
                <Col md={3}>
                  <Button 
                    variant="outline-secondary" 
                    onClick={handleResetSearch}
                    style={{borderRadius: 10, width: '100%'}}
                  >
                    🔄 重置
                  </Button>
                </Col>
              </Row>
            </Form>

            {/* 高级筛选 */}
            {showAdvancedSearch && (
              <div className="mt-4 pt-3" style={{borderTop: '1px solid #e0e0e0'}}>
                <Row>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>👤 角色筛选</Form.Label>
                      <Form.Select
                        value={roleFilter}
                        onChange={e => setRoleFilter(e.target.value)}
                        style={{borderRadius: 8}}
                      >
                        <option value="all">全部角色</option>
                        <option value="player">玩家</option>
                        <option value="admin">管理员</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>📊 状态筛选</Form.Label>
                      <Form.Select
                        value={statusFilter}
                        onChange={e => setStatusFilter(e.target.value)}
                        style={{borderRadius: 8}}
                      >
                        <option value="all">全部状态</option>
                        <option value="active">正常</option>
                        <option value="inactive">封禁</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>📋 排序字段</Form.Label>
                      <Form.Select
                        value={sortBy}
                        onChange={e => setSortBy(e.target.value)}
                        style={{borderRadius: 8}}
                      >
                        <option value="createdAt">注册时间</option>
                        <option value="username">用户名</option>
                        <option value="currentPoints">积分</option>
                        <option value="silverCoins">银币</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>🔄 排序方式</Form.Label>
                      <Form.Select
                        value={sortOrder}
                        onChange={e => setSortOrder(e.target.value)}
                        style={{borderRadius: 8}}
                      >
                        <option value="desc">降序</option>
                        <option value="asc">升序</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* 统计信息 */}
        <Row className="mb-4">
          <Col md={12}>
            <Card style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: 15
            }}>
              <Card.Body className="text-center">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                  <h5 style={{ margin: 0 }}>📊 用户统计</h5>
                  <Button 
                    variant="outline-light" 
                    size="sm" 
                    onClick={fetchUserStats}
                    disabled={statsLoading}
                    style={{ borderRadius: 20, padding: '4px 12px' }}
                    title="刷新统计数据"
                  >
                    {statsLoading ? '🔄' : '🔄'} 刷新
                  </Button>
                </div>
                <Row className="g-3">
                  <Col xs={6} md className="mb-3">
                    <div className="stat-item">
                      <h3 style={{fontSize: '1.8rem', fontWeight: 'bold', marginBottom: 8}}>
                        {pagination.totalCount || users.length}
                      </h3>
                      <p style={{margin: 0, fontSize: '0.9rem', opacity: 0.9}}>总用户数</p>
                    </div>
                  </Col>
                  <Col xs={6} md className="mb-3">
                    <div className="stat-item">
                      <h3 style={{fontSize: '1.8rem', fontWeight: 'bold', marginBottom: 8}}>
                        {users.filter(u => u.status === 'active').length}
                      </h3>
                      <p style={{margin: 0, fontSize: '0.9rem', opacity: 0.9}}>正常用户</p>
                    </div>
                  </Col>
                  <Col xs={6} md className="mb-3">
                    <div className="stat-item">
                      <h3 style={{fontSize: '1.8rem', fontWeight: 'bold', marginBottom: 8}}>
                        {users.filter(u => u.status === 'inactive').length}
                      </h3>
                      <p style={{margin: 0, fontSize: '0.9rem', opacity: 0.9}}>封禁用户</p>
                    </div>
                  </Col>
                  <Col xs={6} md className="mb-3">
                    <div className="stat-item">
                      <h3 style={{fontSize: '1.8rem', fontWeight: 'bold', marginBottom: 8}}>
                        {Math.floor(userStats.totalRemainingPoints || 0).toLocaleString()}
                      </h3>
                      <p style={{margin: 0, fontSize: '0.9rem', opacity: 0.9}}>总剩余积分</p>
                    </div>
                  </Col>
                  <Col xs={12} md className="mb-3">
                    <div className="stat-item">
                      <h3 style={{fontSize: '1.8rem', fontWeight: 'bold', marginBottom: 8}}>
                        {Math.floor(userStats.totalRemainingSilverCoins || 0).toLocaleString()}
                      </h3>
                      <p style={{margin: 0, fontSize: '0.9rem', opacity: 0.9}}>总剩余银币</p>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* 用户列表 */}
        {users.length === 0 ? (
          <Card className="text-center" style={{borderRadius: 15, padding: 40}}>
            <Card.Body>
              <h4 style={{color: '#888'}}>😔 暂无用户数据</h4>
              <p style={{color: '#aaa'}}>没有找到符合条件的用户</p>
            </Card.Body>
          </Card>
        ) : (
          <>
            <Row>
              {users.map(user => {
                const isActive = user.status === 'active';
                const isException = user.exceptionStatus;
                return (
                  <Col md={6} lg={4} key={user._id} className="mb-4">
                    <Card style={{
                      borderRadius: 15,
                      border: '2px solid #0cf',
                      boxShadow: '0 4px 20px rgba(0,204,255,0.2)',
                      transition: 'all 0.3s ease',
                      background: 'linear-gradient(135deg, #fff 0%, #f0f9ff 100%)'
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.boxShadow = '0 8px 30px rgba(0,204,255,0.4)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,204,255,0.2)';
                    }}>
                      <Card.Header style={{
                        background: isActive ? 'linear-gradient(90deg,#28a745,#20c997)' : 'linear-gradient(90deg,#dc3545,#e83e8c)',
                        color: 'white',
                        borderRadius: '13px 13px 0 0'
                      }}>
                        <div className="d-flex justify-content-between align-items-center">
                          <strong>👤 {user.username}</strong>
                          <Badge bg={isActive ? 'success' : 'danger'}>
                            {isActive ? '正常' : '封禁'}
                          </Badge>
                        </div>
                      </Card.Header>
                      <Card.Body>
                        <div className="user-info mb-3">
                          <p><strong>🆔 ID:</strong> {user._id.slice(-8)}</p>
                          <p><strong>👑 角色:</strong> {user.role === 'admin' ? '管理员' : '玩家'}</p>
                          <p><strong>💰 积分:</strong> <span style={{color: '#28a745', fontWeight: 'bold'}}>{Number(user.currentPoints || 0).toFixed(0)}</span></p>
                          <p><strong>🪙 银币:</strong> <span style={{color: '#ffc107', fontWeight: 'bold'}}>{Number(user.silverCoins || 0).toFixed(0)}</span></p>
                          {isException && (
                            <Badge bg="warning" className="mb-2">⚠️ 异常状态</Badge>
                          )}
                        </div>
                        
                        {/* 操作按钮 */}
                        <div className="d-grid gap-2">
                          <Row>
                            <Col xs={6}>
                              <Button 
                                variant="success" 
                                size="sm" 
                                onClick={() => handleShowPointsModal(user, 'add')}
                                style={{width: '100%', borderRadius: 8}}
                              >
                                ➕ 加分
                              </Button>
                            </Col>
                            <Col xs={6}>
                              <Button 
                                variant="warning" 
                                size="sm" 
                                onClick={() => handleShowPointsModal(user, 'deduct')}
                                style={{width: '100%', borderRadius: 8}}
                              >
                                ➖ 减分
                              </Button>
                            </Col>
                          </Row>
                          <Row className="mt-1">
                            <Col xs={6}>
                              <Button 
                                variant="outline-success" 
                                size="sm" 
                                onClick={() => handleShowSilverCoinsModal(user, 'add')}
                                style={{width: '100%', borderRadius: 8}}
                              >
                                🪙➕ 加银币
                              </Button>
                            </Col>
                            <Col xs={6}>
                              <Button 
                                variant="outline-warning" 
                                size="sm" 
                                onClick={() => handleShowSilverCoinsModal(user, 'deduct')}
                                style={{width: '100%', borderRadius: 8}}
                              >
                                🪙➖ 减银币
                              </Button>
                            </Col>
                          </Row>
                          <Row className="mt-1">
                            <Col xs={4}>
                              <Button 
                                variant="primary" 
                                size="sm" 
                                onClick={() => handleShowEditModal(user)}
                                style={{width: '100%', borderRadius: 8}}
                              >
                                ✏️ 编辑
                              </Button>
                            </Col>
                            <Col xs={4}>
                              <Button 
                                variant={isActive ? 'danger' : 'success'} 
                                size="sm" 
                                onClick={() => handleShowBanModal(user, isActive ? 'ban' : 'unban')}
                                style={{width: '100%', borderRadius: 8}}
                              >
                                {isActive ? '🚫 封禁' : '✅ 解封'}
                              </Button>
                            </Col>
                            <Col xs={4}>
                              <Button 
                                variant="outline-danger" 
                                size="sm" 
                                onClick={() => handleDelete(user._id)}
                                style={{width: '100%', borderRadius: 8}}
                              >
                                🗑️ 删除
                              </Button>
                            </Col>
                          </Row>
                          <Row className="mt-1">
                            <Col xs={6}>
                              <Button 
                                variant="info" 
                                size="sm" 
                                onClick={() => handleShowLogs(user, 1)}
                                style={{width: '100%', borderRadius: 8}}
                              >
                                📋 日志
                              </Button>
                            </Col>
                            <Col xs={6}>
                              <Button 
                                variant="dark" 
                                size="sm" 
                                onClick={() => handleShowLedger(user, 1)}
                                style={{width: '100%', borderRadius: 8}}
                              >
                                💼 账目
                              </Button>
                            </Col>
                          </Row>
                          {isException && (
                            <Button 
                              variant="warning" 
                              size="sm" 
                              onClick={() => handleClearException(user)}
                              style={{borderRadius: 8, marginTop: 8}}
                            >
                              🔧 解除异常
                            </Button>
                          )}
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>

            {/* 分页组件 */}
            {pagination.totalPages > 1 && (
              <Row className="mt-4">
                <Col>
                  <Card style={{borderRadius: 15, background: 'rgba(255,255,255,0.95)'}}>
                    <Card.Body className="text-center">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                          <strong>
                            显示 {((pagination.currentPage - 1) * pagination.limit) + 1} - {Math.min(pagination.currentPage * pagination.limit, pagination.totalCount)} 
                            条，共 {pagination.totalCount} 条
                          </strong>
                        </div>
                        <div>
                          <Form.Select
                            style={{width: 'auto', display: 'inline-block'}}
                            value={pagination.limit}
                            onChange={e => {
                              setPagination(prev => ({...prev, limit: parseInt(e.target.value)}));
                              fetchUsers(1);
                            }}
                          >
                            <option value={10}>每页 10 条</option>
                            <option value={20}>每页 20 条</option>
                            <option value={50}>每页 50 条</option>
                            <option value={100}>每页 100 条</option>
                          </Form.Select>
                        </div>
                      </div>
                      
                      <Pagination className="justify-content-center mb-0">
                        <Pagination.First 
                          disabled={pagination.currentPage === 1}
                          onClick={() => handlePageChange(1)}
                        />
                        <Pagination.Prev 
                          disabled={!pagination.hasPrev}
                          onClick={() => handlePageChange(pagination.currentPage - 1)}
                        />
                        
                        {/* 生成页码 */}
                        {Array.from({length: Math.min(5, pagination.totalPages)}, (_, i) => {
                          let pageNum;
                          if (pagination.totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (pagination.currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (pagination.currentPage >= pagination.totalPages - 2) {
                            pageNum = pagination.totalPages - 4 + i;
                          } else {
                            pageNum = pagination.currentPage - 2 + i;
                          }
                          
                          return (
                            <Pagination.Item
                              key={pageNum}
                              active={pageNum === pagination.currentPage}
                              onClick={() => handlePageChange(pageNum)}
                            >
                              {pageNum}
                            </Pagination.Item>
                          );
                        })}
                        
                        <Pagination.Next 
                          disabled={!pagination.hasNext}
                          onClick={() => handlePageChange(pagination.currentPage + 1)}
                        />
                        <Pagination.Last 
                          disabled={pagination.currentPage === pagination.totalPages}
                          onClick={() => handlePageChange(pagination.totalPages)}
                        />
                      </Pagination>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}
          </>
        )}
      </Container>

      {/* ====== 玩家账目（轮次）Modal ====== */}
      <Modal
        show={showLedgerModal}
        onHide={() => setShowLedgerModal(false)}
        size="xl"
        dialogClassName="premium-ledger-modal"
        backdrop="static"
        centered
      >
        <Modal.Header className="premium-modal-header" closeButton>
          <Modal.Title className="premium-modal-title">
            <div className="title-content">
              <div className="title-icon">💰</div>
              <div className="title-text">
                <h4>账目明细</h4>
                <span className="username">{ledgerUser?.username}</span>
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="premium-modal-body">
          {userRounds.length > 0 ? (
            <>
              {/* 统计概览卡片 */}
              <div className="stats-overview">
                <div className="stat-card total-rounds">
                  <div className="stat-icon">🎮</div>
                  <div className="stat-content">
                    <div className="stat-number">{userRounds.length}</div>
                    <div className="stat-label">总轮次</div>
                  </div>
                </div>
                <div className="stat-card total-bet">
                  <div className="stat-icon">💸</div>
                  <div className="stat-content">
                    <div className="stat-number">
                      {Math.round(userRounds.reduce((sum, r) => sum + (r.betTotal || 0), 0))}
                    </div>
                    <div className="stat-label">总投注</div>
                  </div>
                </div>
                <div className="stat-card total-payout">
                  <div className="stat-icon">💎</div>
                  <div className="stat-content">
                    <div className="stat-number">
                      {Math.round(userRounds.reduce((sum, r) => sum + (r.payoutTotal || 0), 0))}
                    </div>
                    <div className="stat-label">总返还</div>
                  </div>
                </div>
                <div className={`stat-card net-profit ${userRounds.reduce((sum, r) => sum + (r.netProfit || 0), 0) >= 0 ? 'positive' : 'negative'}`}>
                  <div className="stat-icon">📊</div>
                  <div className="stat-content">
                    <div className="stat-number">
                      {Math.round(userRounds.reduce((sum, r) => sum + (r.netProfit || 0), 0))}
                    </div>
                    <div className="stat-label">净盈亏</div>
                  </div>
                </div>
              </div>

              {/* 移动端卡片视图 */}
              <div className="mobile-card-view d-lg-none">
                {userRounds.map((r, index) => (
                  <div key={r._id} className="ledger-card">
                    <div className="card-header">
                      <div className="card-title">
                        <span className="round-id">#{r._id.slice(-4)}</span>
                        <span className="device-name">{r.deviceName || '未知设备'}</span>
                      </div>
                      <div className={`profit-badge ${r.netProfit >= 0 ? 'profit' : 'loss'}`}>
                        {r.netProfit >= 0 ? '+' : ''}{Math.round(r.netProfit || 0)}
                      </div>
                    </div>
                    <div className="card-body">
                      <div className="info-row">
                        <span className="label">⏰ 开始时间</span>
                        <span className="value">{new Date(r.startTime).toLocaleString()}</span>
                      </div>
                      {r.endTime && (
                        <div className="info-row">
                          <span className="label">⏰ 结束时间</span>
                          <span className="value">{new Date(r.endTime).toLocaleString()}</span>
                        </div>
                      )}
                      <div className="info-row">
                        <span className="label">💰 投注金额</span>
                        <span className="value bet">{Math.round(r.betTotal || 0)}</span>
                      </div>
                      <div className="info-row">
                        <span className="label">💎 返还金额</span>
                        <span className="value payout">{Math.round(r.payoutTotal || 0)}</span>
                      </div>
                      {r.reason && (
                        <div className="info-row">
                          <span className="label">📝 结束原因</span>
                          <span className="value reason">{r.reason}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* 桌面端表格视图 */}
              <div className="desktop-table-view d-none d-lg-block">
                <div className="premium-table-container">
                  <Table className="premium-table">
                    <thead>
                      <tr>
                        <th>轮次ID</th>
                        <th>设备</th>
                        <th>开始时间</th>
                        <th>结束时间</th>
                        <th>投注</th>
                        <th>返还</th>
                        <th>净盈亏</th>
                        <th>原因</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userRounds.map((r, index) => (
                        <tr key={r._id} className="table-row">
                          <td className="round-id">#{r._id.slice(-4)}</td>
                          <td className="device-name">{r.deviceName || '未知设备'}</td>
                          <td className="timestamp">{new Date(r.startTime).toLocaleString()}</td>
                          <td className="timestamp">{r.endTime ? new Date(r.endTime).toLocaleString() : '—'}</td>
                          <td className="bet-amount">{Math.round(r.betTotal || 0)}</td>
                          <td className="payout-amount">{Math.round(r.payoutTotal || 0)}</td>
                          <td className={`profit-amount ${r.netProfit >= 0 ? 'positive' : 'negative'}`}>
                            {r.netProfit >= 0 ? '+' : ''}{Math.round(r.netProfit || 0)}
                          </td>
                          <td className="reason">{r.reason || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>

              {/* 高级分页控制 */}
              <div className="premium-pagination">
                <div className="pagination-info">
                  <span>第 {ledgerPage} 页，共 {ledgerTotalPages} 页</span>
                </div>
                <div className="pagination-controls">
                  <Button 
                    className="page-btn prev" 
                    disabled={ledgerPage <= 1} 
                    onClick={goPrevLedgerPage}
                  >
                    <span>⟨</span> 上一页
                  </Button>
                  <Button 
                    className="page-btn next" 
                    disabled={ledgerPage >= ledgerTotalPages} 
                    onClick={goNextLedgerPage}
                  >
                    下一页 <span>⟩</span>
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">📊</div>
              <h4>暂无账目记录</h4>
              <p>该用户还没有任何游戏记录</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="premium-modal-footer">
          <Button 
            variant="outline-secondary" 
            className="close-btn"
            onClick={() => setShowLedgerModal(false)}
          >
            关闭
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ====== 玩家日志 Modal ====== */}
      <Modal
        show={showLogsModal}
        onHide={() => setShowLogsModal(false)}
        size="xl"
        dialogClassName="premium-logs-modal"
        backdrop="static"
        centered
      >
        <Modal.Header className="premium-modal-header" closeButton>
          <Modal.Title className="premium-modal-title">
            <div className="title-content">
              <div className="title-icon">📋</div>
              <div className="title-text">
                <h4>操作日志</h4>
                <span className="username">{logsUser?.username}</span>
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="premium-modal-body">
          {logs.length > 0 ? (
            <>
              {/* 日志统计概览 */}
              <div className="logs-overview">
                <div className="log-stat-card total-logs">
                  <div className="stat-icon">📈</div>
                  <div className="stat-content">
                    <div className="stat-number">{logs.length}</div>
                    <div className="stat-label">总记录数</div>
                  </div>
                </div>
                <div className="log-stat-card success-logs">
                  <div className="stat-icon">✅</div>
                  <div className="stat-content">
                    <div className="stat-number">
                      {logs.filter(log => log.status === 'success').length}
                    </div>
                    <div className="stat-label">成功操作</div>
                  </div>
                </div>
                <div className="log-stat-card total-points">
                  <div className="stat-icon">💰</div>
                  <div className="stat-content">
                    <div className="stat-number">
                      {Math.round(logs.reduce((sum, log) => sum + (log.points || 0), 0))}
                    </div>
                    <div className="stat-label">积分变动</div>
                  </div>
                </div>
              </div>

              {/* 移动端卡片视图 */}
              <div className="mobile-logs-view d-lg-none">
                {logs.map((log, index) => (
                  <div key={log._id} className="log-card">
                    <div className="log-card-header">
                      <div className="log-type">
                        <span className="type-badge">{getLogTypeIcon(log.category)}</span>
                        <span className="type-name">{getLogTypeName(log.category)}</span>
                      </div>
                      <div className={`status-badge ${log.status === 'success' ? 'success' : 'failed'}`}>
                        {log.status === 'success' ? '✓ 成功' : '✗ 失败'}
                      </div>
                    </div>
                    <div className="log-card-body">
                      <div className="log-details">
                        <p className="details-text">{log.details || '无详细信息'}</p>
                      </div>
                      {log.points && log.points !== 0 && (
                        <div className="points-change">
                          <span className="label">积分变动:</span>
                          <span className={`points ${log.points > 0 ? 'positive' : 'negative'}`}>
                            {log.points > 0 ? '+' : ''}{Number(log.points).toFixed(0)}
                          </span>
                        </div>
                      )}
                      <div className="log-timestamp">
                        <span className="time-icon">🕒</span>
                        <span className="time-text">{new Date(log.createdAt).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* 桌面端表格视图 */}
              <div className="desktop-logs-view d-none d-lg-block">
                <div className="premium-logs-table-container">
                  <Table className="premium-logs-table">
                    <thead>
                      <tr>
                        <th>操作类型</th>
                        <th>详细信息</th>
                        <th>积分变动</th>
                        <th>状态</th>
                        <th>操作时间</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((log, index) => (
                        <tr key={log._id} className="log-table-row">
                          <td className="log-type-cell">
                            <div className="type-container">
                              <span className="type-icon">{getLogTypeIcon(log.category)}</span>
                              <span className="type-text">{getLogTypeName(log.category)}</span>
                            </div>
                          </td>
                          <td className="log-details-cell">
                            <div className="details-container">
                              {log.details || '无详细信息'}
                            </div>
                          </td>
                          <td className="points-cell">
                            {log.points && log.points !== 0 ? (
                              <span className={`points-value ${log.points > 0 ? 'positive' : 'negative'}`}>
                                {log.points > 0 ? '+' : ''}{Number(log.points).toFixed(0)}
                              </span>
                            ) : (
                              <span className="no-points">—</span>
                            )}
                          </td>
                          <td className="status-cell">
                            <span className={`status-indicator ${log.status === 'success' ? 'success' : 'failed'}`}>
                              {log.status === 'success' ? '✓ 成功' : '✗ 失败'}
                            </span>
                          </td>
                          <td className="timestamp-cell">
                            {new Date(log.createdAt).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>

              {/* 高级分页控制 */}
              <div className="premium-pagination">
                <div className="pagination-info">
                  <span>第 {logsPage} 页，共 {logsTotalPages} 页</span>
                </div>
                <div className="pagination-controls">
                  <Button 
                    className="page-btn prev" 
                    disabled={logsPage <= 1} 
                    onClick={goPrevLogsPage}
                  >
                    <span>⟨</span> 上一页
                  </Button>
                  <Button 
                    className="page-btn next" 
                    disabled={logsPage >= logsTotalPages} 
                    onClick={goNextLogsPage}
                  >
                    下一页 <span>⟩</span>
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">📋</div>
              <h4>暂无日志记录</h4>
              <p>该用户还没有任何操作记录</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="premium-modal-footer">
          <Button 
            variant="outline-secondary" 
            className="close-btn"
            onClick={() => setShowLogsModal(false)}
          >
            关闭
          </Button>
        </Modal.Footer>
      </Modal>

      {/* =========== 积分操作模态 =========== */}
      <Modal show={showPointsModal} onHide={handleClosePointsModal} dialogClassName="user-mgmt-modal-fix">
        <Modal.Header closeButton>
          <Modal.Title>{modalType === 'add' ? '增加积分' : '减少积分'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="formPoints">
              <Form.Label>积分数量</Form.Label>
              <Form.Control
                type="number"
                min="1"
                value={points}
                onChange={(e) => setPoints(e.target.value)}
                placeholder="请输入积分数量"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClosePointsModal}>取消</Button>
          <Button variant="primary" onClick={handleSubmitPoints}>确认</Button>
        </Modal.Footer>
      </Modal>
      {/* =========== 银币操作模态 =========== */}
      <Modal show={showSilverCoinsModal} onHide={handleCloseSilverCoinsModal} dialogClassName="user-mgmt-modal-fix">
        <Modal.Header closeButton>
          <Modal.Title>{silverCoinsModalType === 'add' ? '增加银币' : '减少银币'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="formSilverCoins">
              <Form.Label>银币数量</Form.Label>
              <Form.Control
                type="number"
                min="1"
                value={silverCoins}
                onChange={(e) => setSilverCoins(e.target.value)}
                placeholder="请输入银币数量"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseSilverCoinsModal}>取消</Button>
          <Button variant="primary" onClick={handleSubmitSilverCoins}>确认</Button>
        </Modal.Footer>
      </Modal>
      {/* =========== 编辑用户模态 =========== */}
      <Modal show={showEditModal} onHide={handleCloseEditModal} dialogClassName="user-mgmt-modal-fix">
        <Modal.Header closeButton>
          <Modal.Title>编辑用户</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="formUsername">
              <Form.Label>用户名</Form.Label>
              <Form.Control
                type="text"
                value={editUsername}
                onChange={(e) => setEditUsername(e.target.value)}
                placeholder="请输入用户名"
                required
              />
            </Form.Group>
            <Form.Group controlId="formRole" className="mt-3">
              <Form.Label>角色</Form.Label>
              <Form.Select
                value={editRole}
                onChange={(e) => setEditRole(e.target.value)}
              >
                <option value="player">玩家</option>
                <option value="admin">管理员</option>
              </Form.Select>
            </Form.Group>
            <Form.Group controlId="formStatus" className="mt-3">
              <Form.Label>状态</Form.Label>
              <Form.Select
                value={editStatus}
                onChange={(e) => setEditStatus(e.target.value)}
              >
                <option value="active">正常</option>
                <option value="inactive">封禁</option>
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseEditModal}>取消</Button>
          <Button variant="primary" onClick={handleSubmitEdit}>确认</Button>
        </Modal.Footer>
      </Modal>
      {/* =========== 封禁/解封用户模态 =========== */}
      <Modal show={showBanModal} onHide={handleCloseBanModal} dialogClassName="user-mgmt-modal-fix">
        <Modal.Header closeButton>
          <Modal.Title>{banAction === 'ban' ? '封禁用户' : '解封用户'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <p>
              确定要{banAction === 'ban' ? '封禁' : '解封'} 用户 <strong>{selectedUser.username}</strong> 吗？
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseBanModal}>取消</Button>
          <Button variant={banAction === 'ban' ? 'danger' : 'success'} onClick={handleSubmitBan}>确认</Button>
        </Modal.Footer>
      </Modal>
      
      <style jsx>{`
        @keyframes fadeInUp { 
          from { opacity:0; transform:translateY(40px);} 
          to {opacity:1; transform:translateY(0);} 
        }
        @keyframes fadeIn { 
          from { opacity:0;} 
          to {opacity:1;} 
        }
        
        @keyframes slideInLeft {
          from { opacity: 0; transform: translateX(-20px); }
          to { opacity: 1; transform: translateX(0); }
        }

        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.02); }
        }

        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        .user-mgmt-modal-fix .modal-dialog {
          margin-top: 160px !important;
        }

        /* ====== 高级账目弹窗样式 ====== */
        .premium-ledger-modal .modal-dialog {
          max-width: 1200px !important;
          margin: 20px auto !important;
        }

        .premium-ledger-modal .modal-content {
          border: none;
          border-radius: 20px;
          background: linear-gradient(135deg, #0f1419 0%, #1a202c 50%, #2d3748 100%);
          box-shadow: 
            0 25px 50px rgba(0, 0, 0, 0.6),
            0 0 0 1px rgba(255, 255, 255, 0.1),
            inset 0 1px 0 rgba(255, 255, 255, 0.1);
          overflow: hidden;
          position: relative;
        }

        .premium-ledger-modal .modal-content::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: 
            radial-gradient(circle at 20% 20%, rgba(59, 130, 246, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, rgba(16, 185, 129, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 40% 70%, rgba(139, 92, 246, 0.05) 0%, transparent 50%);
          pointer-events: none;
          z-index: 1;
        }

        .premium-modal-header {
          background: transparent;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          padding: 24px 32px;
          position: relative;
          z-index: 2;
        }

        .premium-modal-title {
          margin: 0;
          color: #ffffff;
        }

        .title-content {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .title-icon {
          font-size: 2rem;
          background: linear-gradient(135deg, #fbbf24, #f59e0b);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: pulse 2s ease-in-out infinite;
        }

        .title-text h4 {
          margin: 0;
          font-size: 1.5rem;
          font-weight: 700;
          color: #ffffff;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
        }

        .title-text .username {
          font-size: 1rem;
          color: #60a5fa;
          font-weight: 500;
          opacity: 0.9;
        }

        .premium-modal-body {
          padding: 32px;
          position: relative;
          z-index: 2;
          max-height: 70vh;
          overflow-y: auto;
        }

        /* 统计概览卡片 */
        .stats-overview {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 16px;
          margin-bottom: 32px;
        }

        .stat-card {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          padding: 20px;
          display: flex;
          align-items: center;
          gap: 16px;
          backdrop-filter: blur(10px);
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .stat-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
          transition: left 0.5s ease;
        }

        .stat-card:hover {
          transform: translateY(-4px);
          border-color: rgba(255, 255, 255, 0.2);
          box-shadow: 0 12px 30px rgba(0, 0, 0, 0.3);
        }

        .stat-card:hover::before {
          left: 100%;
        }

        .stat-icon {
          font-size: 2rem;
          opacity: 0.8;
        }

        .stat-content {
          flex: 1;
        }

        .stat-number {
          font-size: 1.5rem;
          font-weight: 700;
          color: #ffffff;
          margin-bottom: 4px;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
        }

        .stat-label {
          font-size: 0.85rem;
          color: #94a3b8;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .stat-card.positive .stat-number {
          color: #10b981;
        }

        .stat-card.negative .stat-number {
          color: #ef4444;
        }

        /* 移动端卡片样式 */
        .mobile-card-view {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .ledger-card {
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          overflow: hidden;
          backdrop-filter: blur(10px);
          transition: all 0.3s ease;
          animation: slideInLeft 0.6s ease-out;
        }

        .ledger-card:hover {
          transform: translateY(-2px);
          border-color: rgba(255, 255, 255, 0.2);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
        }

        .ledger-card .card-header {
          background: rgba(255, 255, 255, 0.05);
          padding: 16px 20px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .card-title {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .round-id {
          font-weight: 700;
          color: #60a5fa;
          font-size: 0.9rem;
        }

        .device-name {
          color: #94a3b8;
          font-size: 0.85rem;
          font-weight: 500;
        }

        .profit-badge {
          padding: 6px 12px;
          border-radius: 20px;
          font-weight: 700;
          font-size: 0.85rem;
          text-align: center;
          min-width: 60px;
        }

        .profit-badge.profit {
          background: rgba(16, 185, 129, 0.2);
          color: #10b981;
          border: 1px solid rgba(16, 185, 129, 0.3);
        }

        .profit-badge.loss {
          background: rgba(239, 68, 68, 0.2);
          color: #ef4444;
          border: 1px solid rgba(239, 68, 68, 0.3);
        }

        .ledger-card .card-body {
          padding: 20px;
        }

        .info-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 0;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
        }

        .info-row:last-child {
          border-bottom: none;
        }

        .info-row .label {
          color: #94a3b8;
          font-size: 0.85rem;
          font-weight: 500;
        }

        .info-row .value {
          color: #ffffff;
          font-weight: 600;
          text-align: right;
        }

        .info-row .value.bet {
          color: #fbbf24;
        }

        .info-row .value.payout {
          color: #10b981;
        }

        .info-row .value.reason {
          color: #a78bfa;
          font-style: italic;
        }

        /* 桌面端表格样式 */
        .desktop-table-view {
          animation: fadeIn 0.6s ease-out;
        }

        .premium-table-container {
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          overflow: hidden;
          backdrop-filter: blur(10px);
        }

        .premium-table {
          margin: 0;
          width: 100%;
          color: #ffffff;
        }

        .premium-table thead th {
          background: rgba(255, 255, 255, 0.08);
          color: #94a3b8;
          font-weight: 600;
          font-size: 0.85rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          padding: 16px 20px;
          border: none;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .premium-table tbody tr {
          border: none;
          transition: all 0.3s ease;
        }

        .premium-table tbody tr:hover {
          background: rgba(255, 255, 255, 0.05);
          transform: scale(1.01);
        }

        .premium-table tbody td {
          padding: 16px 20px;
          border: none;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
          font-weight: 500;
        }

        .premium-table .round-id {
          color: #60a5fa;
          font-weight: 700;
        }

        .premium-table .device-name {
          color: #94a3b8;
        }

        .premium-table .timestamp {
          color: #d1d5db;
          font-size: 0.85rem;
        }

        .premium-table .bet-amount {
          color: #fbbf24;
          font-weight: 600;
        }

        .premium-table .payout-amount {
          color: #10b981;
          font-weight: 600;
        }

        .premium-table .profit-amount {
          font-weight: 700;
          font-size: 1rem;
        }

        .premium-table .profit-amount.positive {
          color: #10b981;
        }

        .premium-table .profit-amount.negative {
          color: #ef4444;
        }

        .premium-table .reason {
          color: #a78bfa;
          font-style: italic;
        }

        /* 高级分页控制 */
        .premium-pagination {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 32px;
          padding: 20px 0;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
        }

        .pagination-info {
          color: #94a3b8;
          font-size: 0.9rem;
          font-weight: 500;
        }

        .pagination-controls {
          display: flex;
          gap: 12px;
        }

        .page-btn {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          color: #ffffff;
          padding: 10px 20px;
          border-radius: 10px;
          font-weight: 600;
          font-size: 0.9rem;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .page-btn:hover:not(:disabled) {
          background: rgba(255, 255, 255, 0.1);
          border-color: rgba(255, 255, 255, 0.2);
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
        }

        .page-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .page-btn span {
          font-size: 1.1rem;
          transition: transform 0.2s ease;
        }

        .page-btn.prev:hover span {
          transform: translateX(-2px);
        }

        .page-btn.next:hover span {
          transform: translateX(2px);
        }

        /* ====== 日志弹窗专属样式 ====== */
        .premium-logs-modal .modal-dialog {
          max-width: 1200px !important;
          margin: 20px auto !important;
        }

        .premium-logs-modal .modal-content {
          border: none;
          border-radius: 20px;
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
          box-shadow: 
            0 25px 50px rgba(0, 0, 0, 0.6),
            0 0 0 1px rgba(255, 255, 255, 0.1),
            inset 0 1px 0 rgba(255, 255, 255, 0.1);
          overflow: hidden;
          position: relative;
        }

        .premium-logs-modal .modal-content::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: 
            radial-gradient(circle at 30% 30%, rgba(139, 92, 246, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 70% 70%, rgba(59, 130, 246, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 50% 20%, rgba(16, 185, 129, 0.05) 0%, transparent 50%);
          pointer-events: none;
          z-index: 1;
        }

        .premium-logs-modal .title-icon {
          background: linear-gradient(135deg, #8b5cf6, #6366f1);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        /* 日志统计概览 */
        .logs-overview {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 16px;
          margin-bottom: 32px;
        }

        .log-stat-card {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          padding: 20px;
          display: flex;
          align-items: center;
          gap: 16px;
          backdrop-filter: blur(10px);
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .log-stat-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(139, 92, 246, 0.1), transparent);
          transition: left 0.5s ease;
        }

        .log-stat-card:hover {
          transform: translateY(-4px);
          border-color: rgba(139, 92, 246, 0.3);
          box-shadow: 0 12px 30px rgba(139, 92, 246, 0.2);
        }

        .log-stat-card:hover::before {
          left: 100%;
        }

        /* 移动端日志卡片 */
        .mobile-logs-view {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .log-card {
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          overflow: hidden;
          backdrop-filter: blur(10px);
          transition: all 0.3s ease;
          animation: slideInLeft 0.6s ease-out;
        }

        .log-card:hover {
          transform: translateY(-2px);
          border-color: rgba(139, 92, 246, 0.3);
          box-shadow: 0 8px 25px rgba(139, 92, 246, 0.15);
        }

        .log-card-header {
          background: rgba(255, 255, 255, 0.05);
          padding: 16px 20px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .log-type {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .type-badge {
          font-size: 1.2rem;
        }

        .type-name {
          color: #94a3b8;
          font-size: 0.9rem;
          font-weight: 600;
        }

        .status-badge {
          padding: 4px 12px;
          border-radius: 12px;
          font-weight: 600;
          font-size: 0.8rem;
        }

        .status-badge.success {
          background: rgba(16, 185, 129, 0.2);
          color: #10b981;
          border: 1px solid rgba(16, 185, 129, 0.3);
        }

        .status-badge.failed {
          background: rgba(239, 68, 68, 0.2);
          color: #ef4444;
          border: 1px solid rgba(239, 68, 68, 0.3);
        }

        .log-card-body {
          padding: 20px;
        }

        .log-details {
          margin-bottom: 12px;
        }

        .details-text {
          color: #d1d5db;
          font-size: 0.9rem;
          line-height: 1.5;
          margin: 0;
        }

        .points-change {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
          padding: 8px 12px;
          background: rgba(255, 255, 255, 0.03);
          border-radius: 8px;
        }

        .points-change .label {
          color: #94a3b8;
          font-size: 0.85rem;
        }

        .points-change .points {
          font-weight: 700;
          font-size: 0.9rem;
        }

        .points-change .points.positive {
          color: #10b981;
        }

        .points-change .points.negative {
          color: #ef4444;
        }

        .log-timestamp {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #6b7280;
          font-size: 0.8rem;
        }

        .time-icon {
          opacity: 0.7;
        }

        /* 桌面端日志表格 */
        .desktop-logs-view {
          animation: fadeIn 0.6s ease-out;
        }

        .premium-logs-table-container {
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          overflow: hidden;
          backdrop-filter: blur(10px);
        }

        .premium-logs-table {
          margin: 0;
          width: 100%;
          color: #ffffff;
        }

        .premium-logs-table thead th {
          background: rgba(139, 92, 246, 0.1);
          color: #94a3b8;
          font-weight: 600;
          font-size: 0.85rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          padding: 16px 20px;
          border: none;
          border-bottom: 1px solid rgba(139, 92, 246, 0.2);
        }

        .premium-logs-table tbody tr {
          border: none;
          transition: all 0.3s ease;
        }

        .premium-logs-table tbody tr:hover {
          background: rgba(139, 92, 246, 0.05);
          transform: scale(1.01);
        }

        .premium-logs-table tbody td {
          padding: 16px 20px;
          border: none;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
          font-weight: 500;
        }

        .log-type-cell .type-container {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .log-type-cell .type-icon {
          font-size: 1.2rem;
        }

        .log-type-cell .type-text {
          color: #94a3b8;
          font-weight: 600;
        }

        .log-details-cell .details-container {
          color: #d1d5db;
          font-size: 0.9rem;
          max-width: 300px;
          word-wrap: break-word;
        }

        .points-cell .points-value {
          font-weight: 700;
          font-size: 1rem;
        }

        .points-cell .points-value.positive {
          color: #10b981;
        }

        .points-cell .points-value.negative {
          color: #ef4444;
        }

        .points-cell .no-points {
          color: #6b7280;
          font-style: italic;
        }

        .status-cell .status-indicator {
          padding: 4px 12px;
          border-radius: 12px;
          font-weight: 600;
          font-size: 0.8rem;
        }

        .status-cell .status-indicator.success {
          background: rgba(16, 185, 129, 0.2);
          color: #10b981;
        }

        .status-cell .status-indicator.failed {
          background: rgba(239, 68, 68, 0.2);
          color: #ef4444;
        }

        .timestamp-cell {
          color: #94a3b8;
          font-size: 0.85rem;
        }

        /* 空状态样式 */
        .empty-state {
          text-align: center;
          padding: 60px 40px;
          color: #94a3b8;
        }

        .empty-icon {
          font-size: 4rem;
          margin-bottom: 24px;
          opacity: 0.6;
        }

        .empty-state h4 {
          color: #ffffff;
          margin-bottom: 12px;
          font-weight: 600;
        }

        .empty-state p {
          color: #6b7280;
          font-size: 0.9rem;
        }

        /* 底部按钮样式 */
        .premium-modal-footer {
          background: transparent;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          padding: 24px 32px;
          position: relative;
          z-index: 2;
        }

        .premium-modal-footer .close-btn {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          color: #ffffff;
          padding: 12px 24px;
          border-radius: 10px;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .premium-modal-footer .close-btn:hover {
          background: rgba(255, 255, 255, 0.1);
          border-color: rgba(255, 255, 255, 0.2);
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
        }
        
        @media (max-width: 768px) {
          .user-mgmt-modal-fix .modal-dialog {
            margin-top: 60px !important;
          }

          .premium-ledger-modal .modal-dialog,
          .premium-logs-modal .modal-dialog {
            margin: 10px !important;
            max-width: calc(100vw - 20px) !important;
          }

          .premium-modal-header,
          .premium-modal-body,
          .premium-modal-footer {
            padding: 20px;
          }

          .stats-overview,
          .logs-overview {
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
            margin-bottom: 24px;
          }

          .stat-card,
          .log-stat-card {
            padding: 16px;
            flex-direction: column;
            text-align: center;
            gap: 8px;
          }

          .stat-number {
            font-size: 1.2rem !important;
          }

          .stat-label {
            font-size: 0.75rem !important;
          }

          .premium-pagination {
            flex-direction: column;
            gap: 16px;
            text-align: center;
          }

          .pagination-controls {
            justify-content: center;
          }

          .title-content {
            gap: 12px;
          }

          .title-icon {
            font-size: 1.5rem;
          }

          .title-text h4 {
            font-size: 1.2rem;
          }

          /* 日志卡片移动端优化 */
          .log-card-header {
            padding: 12px 16px;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }

          .log-card-body {
            padding: 16px;
          }

          .points-change {
            flex-direction: column;
            align-items: flex-start;
            gap: 4px;
          }

          .log-timestamp {
            font-size: 0.75rem;
          }
        }

        @media (max-width: 480px) {
          .stats-overview,
          .logs-overview {
            grid-template-columns: 1fr;
          }

          .page-btn {
            padding: 8px 16px;
            font-size: 0.85rem;
          }

          .log-type {
            flex-direction: column;
            align-items: flex-start;
            gap: 4px;
          }

          .type-badge {
            font-size: 1rem;
          }

          .type-name {
            font-size: 0.8rem;
          }

          .status-badge {
            font-size: 0.75rem;
            padding: 3px 8px;
          }
        }
        
        .stat-item {
          padding: 15px 10px;
          min-height: 80px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          border-radius: 8px;
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          transition: all 0.3s ease;
        }
        
        .stat-item:hover {
          background: rgba(255, 255, 255, 0.2);
          transform: translateY(-2px);
        }
        
        .stat-item h3 {
          margin: 0;
          font-size: 1.8rem;
          font-weight: bold;
          color: #fff;
          text-shadow: 0 2px 4px rgba(0,0,0,0.3);
        }
        
        .stat-item p {
          margin: 8px 0 0 0;
          opacity: 0.9;
          font-size: 0.9rem;
          font-weight: 500;
          color: #fff;
          letter-spacing: 0.5px;
        }
        
        @media (max-width: 768px) {
          .stat-item {
            padding: 12px 8px;
            min-height: 70px;
          }
          
          .stat-item h3 {
            font-size: 1.5rem;
          }
          
          .stat-item p {
            font-size: 0.8rem;
          }
        }
        
        .user-info p {
          margin: 8px 0;
          padding: 5px 0;
          border-bottom: 1px solid rgba(0,204,255,0.1);
        }
        
        .user-info p:last-child {
          border-bottom: none;
        }
        
        .card:hover {
          transform: translateY(-5px) !important;
          box-shadow: 0 8px 30px rgba(0,204,255,0.4) !important;
        }
      `}</style>
    </div>
    </>
  );
};

export default UserManagement;
